import { Button, Checkbox, Input, message, Typography } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import LilyButton from "../../../../../components/button/lily-button";
import { useClaimContext } from "../../component/claim-context";
import LilyLoading from "../../../../../components/loading/lily-loading";
import { useNavigate } from "react-router-dom";
import portalAPI from "../../../../../api/api";
import {
  CLAIM_SUBMISSION_URL,
  UPLOAD_CONTENT_URL,
} from "../../../../../api/URLs";
import SignatureComponenet from "../../../../../components/signature/signature.component";
import moment from "moment";
import { fillPdf } from "../../../../../exports/preview-form-pdf";
import { formatWrapAddress } from "../../../../../exports/util";

const ClaimConfirmation = (props) => {
  const { caseNumber } = props;
  const { Title, Text } = Typography;
  const { TextArea } = Input;
  const [isSubmitted, setIsSubmitted] = useState(false);
  const navigate = useNavigate();
  const [count, setCount] = useState(5);
  const {
    claimForm,
    setCurrent,
    setClaimForm,
    supportOptions,
    setIsBlocking,
    pdfFormData,
    setPdfFormData,
  } = useClaimContext();
  const [submitting, setSubmitting] = useState(false);
  const [filledPdfUrl, setFilledPdfUrl] = useState(null);
  const [isGenerated, setIsGenerated] = useState(false);
  const [signing, setSigning] = useState(false);
  const [error, setError] = React.useState("");
  const [achTnc, setAchTnc] = React.useState(false);
  const [isAch, setIsAch] = React.useState(false);


  React.useEffect(() => { setError('') }, [achTnc])

  React.useEffect(() => {
    if (claimForm?.signature?.signatureLink) {
      setError("");
    }
  }, [claimForm?.signature?.signatureLink]);

  useEffect(() => {

    if (claimForm.paymentInfo.method === "direct-deposit") {
      setIsAch(true);
    } else {
      setIsAch(false);
    }

    const loadData = async (pdfFormData) => {
      try {
        if (pdfFormData["Signature19_es_:signer:signature"] !== "") {
          const pdfUrl = await fillPdf(pdfFormData);
          setFilledPdfUrl(pdfUrl);
          setIsGenerated(true);
        } else {
          setIsGenerated(false);
        }
      } catch (err) {
        message.error("Error loading data:", err);
      }
    };

    loadData(pdfFormData);

    return () => {
      if (filledPdfUrl) URL.revokeObjectURL(filledPdfUrl);
    };
  }, [pdfFormData, claimForm]);

  const handleSignature = async (signatureLink, url, s3key) => {
    let entityPdfFormData = {
      ...pdfFormData,
      "Signature19_es_:signer:signature": signatureLink || "",
      Date: signatureLink ? moment().format("MM/DD/YYYY HH:mm [EST]") : "",
    };

    setClaimForm((prev) => ({
      ...prev,
      signature: {
        s3key: s3key,
        url: url || "",
        signatureLink: signatureLink || "",
        signedAt: signatureLink !== "" ? moment() : null,
      },
    }));

    setPdfFormData(entityPdfFormData);
  };

  const generatePreviewForm = async (url) => {
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error("Failed to fetch PDF.");
      const blob = await response.blob();

      const pdfFormData = new FormData();
      pdfFormData.append("file", blob, "filled-form.pdf");
      pdfFormData.append("folderType", "patient-signature");

      const { data } = await portalAPI.post(UPLOAD_CONTENT_URL, pdfFormData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const s3Key = data?.data?.s3key ?? "";
      const fileName = s3Key.split("dev/claim/")[1] ?? "";

      const fileBody = {
        fileName,
        s3Key,
        fileType: blob.type,
        url: data?.data?.url ?? "",
        contentDocumentId: data?.data?.contentDocumentId || "",
      };
      return fileBody;
    } catch (error) {
      message.error("Failed to generate preview form.");
      return null;
    }
  };

  const handleSubmitClaim = async () => {
    var errmsg = "";

    //* check signature;
    if (claimForm?.signature?.signatureLink === "") {
      errmsg = "Signature is required to submit a claim.";
    }

    //* check ach t&c
    if (!achTnc && isAch) {
      if (errmsg === "") {
        errmsg =
          "Please make sure you have agreed to the ACH Terms and Conditions.";
      } else {
        errmsg = `${errmsg}${"#NEWLINE#"}Please make sure you have agreed to the ACH Terms and Conditions.`;
      }
    }

    if (errmsg) {
      setError(errmsg);
      return;
    }

    setSubmitting(true);

    try {
      let previewFormLink = await generatePreviewForm(filledPdfUrl);
      if (!previewFormLink) {
        return message.warning("No Preview Form PDF available");
      }

      const body = {
        ...claimForm,
        amountRequested: Number(claimForm?.insuranceInfo?.copayAmount),
        previewFormLink: [previewFormLink],
      };

      const res = await portalAPI.post(CLAIM_SUBMISSION_URL, body);

      if (res?.data?.status === 200) {
        setIsBlocking(false);
        setIsSubmitted(true);
      } else {
        message.error(
          res?.data?.message || "Submission failed. Please try again."
        );
      }
    } catch (error) {
      message.error("Failed to submit claim. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (!isSubmitted) return;
    if (count === 0) {
      navigate("/dashboard"); // Change this route
      return;
    }

    const timer = setTimeout(() => {
      setCount((prev) => prev - 1);
    }, 1000); // 1 second

    return () => clearTimeout(timer); // Cleanup on unmount
  }, [isSubmitted, count, navigate]);

  const disabledSubmit = useMemo(() => {
    if (isGenerated) {
      return false;
    }
    return true;
  }, [isGenerated]);

  return (
    <div>
      {!isSubmitted ? (
        <div className="d-flex flex-column gap-4">
          <Title className="mb-1 main-title" level={3}>
            Confirm Final Details
          </Title>
          <div className="d-flex flex-column gap-2">
            <Text className="claim-text-label">
              You uploaded the following documents:
            </Text>
            {supportOptions &&
              supportOptions.map((item) => {
                return (
                  <Checkbox
                    checked={item.checked}
                    value={item.value}
                    onChange={() => { }}
                  >
                    {item.label}
                  </Checkbox>
                );
              })}
          </div>
          {claimForm.paymentInfo.method && (
            <div className="d-flex flex-column gap-2">
              <Text className="claim-text-label">Reimbursement Method:</Text>
              {(() => {
                switch (claimForm.paymentInfo.method) {
                  case "direct-deposit":
                    return (
                      <Text className="claim-text-label">
                        Direct Deposit{" "}
                        {claimForm.paymentInfo.directDeposit.accountNumber ||
                          ""}
                      </Text>
                    );
                  case "check-by-mail":
                    let address = claimForm.paymentInfo.checkByMail;
                    return (
                      <div className="d-flex flex-column">
                        <Text>Check by Mail</Text>
                        <div className="d-flex flex-column mt-3">
                          <Text className="claim-text-label">Address:</Text>
                          {formatWrapAddress({
                            line1: address.address1,
                            line2: address.address2,
                            city: address.city,
                            state: address.state,
                            zip: address.zip,
                          })}
                        </div>
                      </div>
                    );
                  default:
                    return null;
                }
              })()}
            </div>
          )}
          <div className="d-flex flex-column gap-2">
            <Text style={{ fontWeight: 500, fontSize: "20px" }}>Comments</Text>
            <TextArea
              name="comments"
              rows={6}
              value={claimForm.comments}
              onChange={(e) =>
                setClaimForm({ ...claimForm, comments: e.target.value })
              }
            />
          </div>

          {isAch && <div className="d-flex flex-column gap-2">
            <Text style={{ fontWeight: 500, fontSize: "20px" }}>
              ACH Confirmation <span style={{ color: "#ff4d4f" }}>*</span>
            </Text>

            <div>
              <Checkbox
                checked={achTnc}
                value={`asjdoiasjdoiasd`}
                onChange={() => setAchTnc(o => !o)}
              />
              <span style={{ marginLeft: 8 }}>I agree to the <span
                onClick={() => window.open(`https://documents.paysign.com/en/portal/achtransferverbiage/achtransferverbiage.pdf`)}
                style={{
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  color: '#0000EE'
                }}>
                ACH Terms and Conditions
              </span></span>
            </div>
          </div>}

          <div className="d-flex flex-column gap-2">
            <Text style={{ fontWeight: 500, fontSize: "20px" }}>
              Signature <span style={{ color: "#ff4d4f" }}>*</span>
            </Text>
            <Text>
              By signing below, I certify: (1) The information I am submitting
              in support of my claim for reimbursement is true and accurate; (2)
              The expenses for which I am requesting reimbursement were actually
              incurred and are not eligible for payment/reimbursement from my
              medical or prescription insurance; (3) I am not enrolled in any
              state, federal, or government funded healthcare program,
              including, without limitation, Medicaid, Medicare, Medicare Part
              D, Medicare Advantage, Medigap, DoD, VA, TRICARE®/CHAMPUS, or any
              state prescription drug assistance program; and (4) I did not pay
              for these expenses with a flexible savings account (FSA), health
              savings account (HSA), or health reimbursement account (HRA). In
              support of my claim for reimbursement of my pharmacy expenses, I
              authorize Lilly USA, LLC and its agents to contact my pharmacy or
              other healthcare providers to disclose and obtain information
              about the pharmacy claim for which I am seeking reimbursement. A
              copy of this authorization is as valid as the original, and this
              authorization will be valid even if I sign it electronically.
            </Text>
          </div>

          <SignatureComponenet
            singedLink={claimForm?.signature?.signatureLink || ""}
            handleSignature={handleSignature}
            submitting={submitting}
            setSigning={setSigning}
          />

          <div className="d-flex flex-column">
            <Text className="claim-text-label">Signature Date:</Text>
            <Text>
              {claimForm?.signature?.signedAt
                ? moment(claimForm?.signature?.signedAt).format(
                  "MM/DD/YYYY HH:mm [EST]"
                )
                : "N/A"}
            </Text>
          </div>

          <div className="d-flex flex-row justify-content-end mt-5">
            <Button
              style={{ width: "150px", marginRight: "12px" }}
              color="default"
              variant="link"
              disabled={signing}
              onClick={() => setCurrent(2)}
            >
              Back
            </Button>

            <LilyButton
              style={{ minWidth: "200px", padding: "0 20px" }}
              onClick={handleSubmitClaim}
              loading={submitting}
              disabled={signing}
              text={`${caseNumber ? "Resubmit as a New Claim" : "Submit Claim"}`}
            />
          </div>

          {error ? (
            <div
              style={{
                display: "flex",
                textAlign: "end",
                justifyContent: "flex-end",
                flexDirection: 'column'
              }}
            >
              {
                error.split('#NEWLINE#').map((et, eti) => <Text type="danger" key={`error-msg-${eti}`}>
                  {et}
                </Text>)
              }
            </div>
          ) : null}
        </div>
      ) : (
        <div className="d-flex flex-column align-items-center justify-content-center h-100">
          <Title className="mb-1" level={3}>
            Your claim has been submitted!
          </Title>
          <Text className="description">
            Redirecting to your dashboard in {count} second
            {count !== 1 ? "s" : ""}...
          </Text>
          <LilyLoading />
        </div>
      )}
    </div>
  );
};

export default ClaimConfirmation;
